import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Divider
} from "@mui/material";
import { a11yProps, TabPanel } from "../../../ui";
import { Colors } from "../../../../styles/theme";
import GeneralTab, { getDefaultGeneralValues } from "./GeneralTab";
import ObjectiveTab, { getDefaultObjectiveValues } from "./ObjectiveTab";
import ExternTab, { getDefaultExternValues } from "./ExternTab";
import InternTab, { getDefaultInternValues } from "./InternTab";
import RefractTab, { getDefaultRefractValues } from "./RefractTab";
import DiagnosisTab, { getDefaultDiagnosisValues } from "./DiagnosisTab";
import { EyeExamService } from "../../../../service/eyeExamService";
import AssistPanel from "./assist/AssistPanel";
import TemplateDialog from "./TemplateDialog";
import { toast } from "react-toastify";
import { useCan } from "hooks/useCan";
import { useLocation } from "hooks/useLocation";
import { toTitleCase } from "utils/index";

/**
 * EyeExamDialog
 * - Modal dialog to create or edit a patient's eye exam
 * - Displays a patient header and 6 tabs: General, Obj, Refract., Extern., Intern., Diag.
 * - For now, each tab contains placeholder content we will fill in future steps.
 *
 * Props
 * - open: boolean controls visibility
 * - onClose: function(event, reason?) to close dialog (provided by useDialogModal)
 * - listener: optional callback for parent to receive save/cancel actions (from useDialogModal)
 * - mode: 'create' | 'edit'
 * - patient: minimal patient object { fullName, birthdate, age, healthcard?, mobile? }
 */
export default function EyeExamDialog({ open, onClose, listener, patient, customerId: customerIdProp, practitionerId: practitionerIdProp }) {
  const [tab, setTab] = React.useState(0);
  const [general, setGeneral] = React.useState(getDefaultGeneralValues());
  const [objective, setObjective] = React.useState(getDefaultObjectiveValues());
  const [refract, setRefract] = React.useState(getDefaultRefractValues());
  const [extern, setExtern] = React.useState(getDefaultExternValues());
  const [intern, setIntern] = React.useState(getDefaultInternValues());
  const [diagnosis, setDiagnosis] = React.useState(getDefaultDiagnosisValues());
  const [saving, setSaving] = React.useState(false);
  const [assistOpen, setAssistOpen] = React.useState(false);
  const [templatesOpen, setTemplatesOpen] = React.useState(false);
  const can = useCan();
  const { selectedLocation } = useLocation();
  const canAddEyeExam = can.action('eyeexam', 'create');
  
  // Basic, safe fallbacks for patient header
  const header = useMemo(() => ({
    name: patient?.fullName || "",
    dob: patient?.birthdate || "",
    age: typeof patient?.age === 'number' ? `${patient.age} yrs` : "",
    healthcard: patient?.healthcard || patient?.healthCard || "",
    phone: patient?.mobile || patient?.phone || "",
  }), [patient]);

  const handleSave = async () => {
    console.log('locaton', selectedLocation);
    // Build payload with General/Objective/Refract; denormalize chiefComplaint for list/search
  const payload = { general, objective, refract, extern, intern, diagnosis };
    const chiefComplaint = general?.complaint || null;
  const customerId = customerIdProp || patient?.id || patient?.customerId;
    const practitionerId = practitionerIdProp || null;

    if (!customerId) {
      // Cannot save without a customer id
      if (listener) listener({ action: 'error', message: 'Missing customerId for eye exam create' });
      return;
    }

    try {
      setSaving(true);
      const created = await EyeExamService.create({
        customerId,
        practitionerId,
        locationId: selectedLocation?.id || null,
        chiefComplaint,
        payload
      });
      toast.success('Eye exam saved successfully');
      if (listener) listener({ action: 'save', data: { exam: created } });
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (listener) listener({ action: 'cancel' });
    onClose?.();
  };

  const applyTemplate = (tplPayload) => {
    if (!tplPayload) return;
    // Merge per tab to avoid blowing away state; template may have subset
    setGeneral(prev => ({ ...prev, ...(tplPayload.general || {}) }));
    setObjective(prev => ({ ...prev, ...(tplPayload.objective || {}) }));
    setRefract(prev => ({ ...prev, ...(tplPayload.refract || {}) }));
    setExtern(prev => ({ ...prev, ...(tplPayload.extern || {}) }));
    setIntern(prev => ({ ...prev, ...(tplPayload.intern || {}) }));
    setDiagnosis(prev => ({ ...prev, ...(tplPayload.diagnosis || {}) }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
  <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>New Eye Exam</span>
          {/* <IconButton size="small" onClick={() => setAssistOpen(true)} title="Open Assist">
            <AssistantIcon />
          </IconButton> */}
          <Button size="small" variant="outlined" onClick={() => setTemplatesOpen(true)} sx={{ ml: 1 }}>Templates</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Patient header */}
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{toTitleCase(header.name)}</Typography>
          {header.dob && (
            <Typography variant="body2" color={Colors.dim_grey}>DOB: {header.dob} {header.age && `(${header.age})`}</Typography>
          )}
          {header.healthcard && (
            <Typography variant="body2" color={Colors.dim_grey}>Health Card: {header.healthcard}</Typography>
          )}
          {header.phone && (
            <Typography variant="body2" color={Colors.dim_grey}>Phone: {header.phone}</Typography>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
          
        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="Eye Exam Tabs"
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label="General" {...a11yProps('eyeexam', 0)} />
          <Tab label="Obj" {...a11yProps('eyeexam', 1)} />
          <Tab label="Refract." {...a11yProps('eyeexam', 2)} />
          <Tab label="Extern." {...a11yProps('eyeexam', 3)} />
          <Tab label="Intern." {...a11yProps('eyeexam', 4)} />
          <Tab label="Diag." {...a11yProps('eyeexam', 5)} />
        </Tabs>

        {/* Panels. We'll flesh others out later. */}
        <TabPanel idfor="eyeexam" value={tab} index={0}>
          <GeneralTab value={general} onChange={setGeneral} />
        </TabPanel>
        <TabPanel idfor="eyeexam" value={tab} index={1}>
          <ObjectiveTab value={objective} onChange={setObjective} />
        </TabPanel>
        <TabPanel idfor="eyeexam" value={tab} index={2}>
          <RefractTab
            value={refract}
            onChange={setRefract}
            onCopyToDiagnosisRx={(patch) => {
              // patch shape: { 'Sphere': {od,os}, 'Cylinder': {od,os}, 'Axis': {od,os}, 'Add': {od,os}, 'Prism': {od,os} }
              setDiagnosis((prev) => {
                const next = { ...prev, rx: { ...prev.rx } };
                Object.keys(patch || {}).forEach((col) => {
                  next.rx[col] = { ...(next.rx?.[col] || {}), ...patch[col] };
                });
                // if rx date empty, set to today
                if (!next.date) {
                  next.date = new Date().toISOString().split('T')[0];
                }
                return next;
              });
            }}
          />
        </TabPanel>
        <TabPanel idfor="eyeexam" value={tab} index={3}>
          <ExternTab value={extern} onChange={setExtern} />
        </TabPanel>
        <TabPanel idfor="eyeexam" value={tab} index={4}>
          <InternTab value={intern} onChange={setIntern} />
        </TabPanel>
        <TabPanel idfor="eyeexam" value={tab} index={5}>
          <DiagnosisTab value={diagnosis} onChange={setDiagnosis} />
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleCancel} disabled={saving}>Cancel</Button>
        {canAddEyeExam && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        )}
      </DialogActions>
      <AssistPanel open={assistOpen} onClose={() => setAssistOpen(false)} general={general} objective={objective} />
      <TemplateDialog
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        practitionerId={practitionerIdProp}
        currentPayload={{ general, objective, refract, extern, intern, diagnosis }}
        onApplyTemplate={applyTemplate}
      />
    </Dialog>
  );
}
